<template>
  <UIDialog
    :is-open
    :z-index
    :title="assetIds.length ? `Share assets (${assetIds.length})` : 'Share whole repository'"
    @close="handleClose"
  >
    <p class="repository-collaborators-assets-tree__description">
      Select assets to share with the collaborator:
    </p>
    <UISkeletonTree v-if="isLoading" message="Loading tree..." />
    <UITree
      v-else-if="data"
      v-model:search="search"
      :data
      :meta-fields="['amount', 'balance', 'path']"
      :root="AnalyticsField.REPO_ROOT"
      :formatter
      :get-description
      hidable-names
      sort-field="balance"
      size="xs"
      wide
      placeholder="Search assets..."
      class="repository-collaborators-assets-tree"
      @click:node="handleClickNode"
    >
      <template #name="{ isRoot }">
        <template v-if="isRoot">{{ repoName }}</template>
      </template>
      <template #before-item="{ keyValue }">
        <UICheckbox
          v-if="keyValue !== AnalyticsField.REPO_ROOT"
          v-model="assetIds"
          :value="keyValue"
          class="repository-collaborators-assets-tree__checkbox"
          :class="getCheckboxClasses(keyValue)"
          @click.stop
        />
        <div v-else class="w-4 shrink-0" />
      </template>
    </UITree>
    <div v-else class="repository-collaborators-assets-tree__no-data">
      <UILayoutNoData hide-question-icon inline />
    </div>
    <UIButtonsGroup>
      <UIButton label="Cancel" variant="gray" fill="outlined" @click="handleClose" />
      <UIButton label="Done" variant="primary" @click="handleDone" />
    </UIButtonsGroup>
  </UIDialog>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { pick } from 'lodash'

import { AnalyticsField, AnalyticsTree, Asset, ModalClass } from '@types'

import { UNKNOWN_VALUE } from '@/const'

import useAnalyticsStore from '@/store/analytics'
import { useAssetsStore } from '@/store/assets'
import { useRepositoriesStore } from '@/store/repositories'

import { handleCatchedError } from '@/helpers/common'
import { prepareTree } from '@/store/analytics/tree/utils/helpers'

import { UICheckbox } from '@ui/controllers'
import { UILayoutNoData, UITree } from '@ui/core'
import { UIDialog } from '@ui/modals'
import { UIButton, UIButtonsGroup } from '@ui/buttons'
import { UISkeletonTree } from '@ui/skeletons'

type Props = {
  modal: ModalClass<any>
  assetIds: string[]
  repositoryId: string
}

type Emits = {
  (e: 'done', data: string[]): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const analyticsStore = useAnalyticsStore()
const assetsStore = useAssetsStore()
const repositoriesStore = useRepositoriesStore()
  
const search = ref<string>('')

const isLoading = ref(false)
const tree = ref<AnalyticsTree[]>()
const assets = ref<Asset[]>()
const assetIds = ref<string[]>([...props.assetIds])

const zIndex = computed(() => props.modal.zIndex)
const isOpen = computed(() => props.modal.isOpened)

const repository = computed(() => repositoriesStore.getList.find(item => item.id === props.repositoryId))
const repoName = computed(() => repository.value?.name)
const repoParams = computed(() => {
  const params = pick(repository.value?.user_repo_settings, ['periods', 'freq', 'end', 'include_end', 'end_is_today'])
  return {
    ...params,
    currency: repository.value?.currency,
  } as typeof analyticsStore.getAnalyticsParams
})

const data = computed(() => prepareTree(tree.value))

const isSelectedItem = (id: string) => {
  return !!assetIds.value.find(item => item === id)
}

const getCheckboxClasses = (keyValue: string) => {
  return isSelectedItem(keyValue) ? 'repository-collaborators-assets-tree__checkbox--checked' : ''
}

const handleClose = () => {
  props.modal.close()
}

const handleClickNode = (path: string[]) => {
  const assetId = path.at(-1)
  if (!assetId || assetId === AnalyticsField.REPO_ROOT) return
  if (isSelectedItem(assetId)) {
    assetIds.value = assetIds.value.filter(item => item !== assetId)
  } else {
    assetIds.value = [...new Set([...assetIds.value, assetId])]
  }
}

const formatter = (key: string) => {
  const asset = assets.value?.find(item => item.id === key)
  return asset?.name || UNKNOWN_VALUE
}

const getDescription = (key: string) => {
  const asset = assets.value?.find(item => item.id === key)
  return asset?.ticker || UNKNOWN_VALUE
}

const handleDone = () => {
  emit('done', assetIds.value)
  handleClose()
}

const fetchTree = async () => {
  try {
    const result = await analyticsStore.fetch({
      data: 'tree',
    }, props.repositoryId, repoParams.value)
    tree.value = result.tree_view
  } catch (e) {
    handleCatchedError(e as string, { id: repositoriesStore.getCurrentId })
  }
}

const fetchAssets = async () => {
  try {
    const result = await assetsStore.fetch(props.repositoryId, false)
    assets.value = result
  }  catch (e) {
    handleCatchedError(e as string, { id: repositoriesStore.getCurrentId })
  }
}

onMounted(async () => {
  isLoading.value = true
  await fetchAssets()
  await fetchTree()
  isLoading.value = false
})
</script>

<style>
.repository-collaborators-assets-tree {
  @apply h-[30rem];

  .ui-tree__search {
    @apply gap-x-1;
    @apply px-0.5;
  }

  .ui-tree__container {
    @apply flex flex-col;
    @apply gap-1 px-0.5 pb-1 -mr-1;
  }

  .ui-tree__item-area {
    @apply items-start justify-between;
    @apply pt-[0.45rem] pl-2 gap-x-2;

    &--root {
      @apply pb-2;
      @apply items-center;
    }
  }

  .ui-tree__branch--l {
    @apply before:-top-[calc(0.125rem+1px)];
  }

  .ui-tree__branch--i,
  .ui-tree__branch--x {
    @apply before:-top-[calc(0.125rem+1px)];
  }

  &__checkbox {
    @apply m-0 mt-2;
    @apply opacity-0;
  }

  .ui-tree__item {
    &:has(.repository-collaborators-assets-tree__checkbox--checked) {
      @apply !bg-indigo-50 dark:!bg-gray-900;
      @apply outline outline-1 outline-indigo-100 dark:outline-gray-800;
    }
  }

  .ui-tree__item:hover &__checkbox,
  &__checkbox--checked {
    @apply opacity-100;
  }

  &__no-data {
    @apply flex flex-col flex-auto justify-center;
  }

  &__description {
    @apply text-sm;
  }

}
</style>
